import { useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { Save, Eye, Loader2, RefreshCw, Plus, Download } from 'lucide-react';
import {
  BLOCK_META,
  BLOCK_TYPES,
  AI_PROVIDERS,
  LEARNING_REASONS,
  LEARNING_FOCUS,
} from '@ellie/shared';
import type { Block, BlockType } from '@ellie/shared';
import { useStudioStore } from '@/stores/useStudioStore';
import type { ComparisonResult } from '@/stores/useStudioStore';
import { SortableBlock } from '@/components/editor/SortableBlock';
import { BlockEditor } from '@/components/editor/BlockEditor';
import { Link } from 'react-router-dom';

export function StudioResults() {
  const {
    workbook,
    blocks,
    reorderBlocks,
    updateBlock,
    deleteBlock,
    saveWorkbook,
    saving,
    regenerateBlock,
    regeneratingBlockId,
    generationResult,
    downloadPdf,
    downloading,
    comparisonResults,
    profile,
  } = useStudioStore();

  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);
  const [showAddBlock, setShowAddBlock] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const selectedBlock = blocks.find((b) => b.id === selectedBlockId) ?? null;

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = blocks.findIndex((b) => b.id === active.id);
      const newIndex = blocks.findIndex((b) => b.id === over.id);
      reorderBlocks(arrayMove(blocks, oldIndex, newIndex));
    }
  }

  if (!workbook) return null;

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border bg-white px-4 py-3">
        <div>
          <h2 className="font-heading text-lg text-text-primary">{workbook.title}</h2>
          <p className="text-xs text-text-muted">
            {workbook.theme} &middot; {blocks.length} blocks
            {generationResult && (
              <>
                {' '}
                &middot; {(generationResult.duration_ms / 1000).toFixed(1)}s &middot; $
                {generationResult.tokens.estimated_cost_usd.toFixed(4)}
                {generationResult.provider && (
                  <>
                    {' '}
                    &middot;{' '}
                    <span className="rounded bg-gray-100 px-1 py-0.5 font-mono text-[10px]">
                      {generationResult.provider}/{generationResult.model}
                    </span>
                  </>
                )}
              </>
            )}
          </p>
          {/* Personalization badges */}
          <div className="mt-1 flex flex-wrap gap-1.5">
            {profile.learning_reason && (
              <span className="rounded-full bg-purple-100 px-2 py-0.5 text-[10px] font-medium text-purple-700">
                {LEARNING_REASONS.find((r) => r.code === profile.learning_reason)?.name ??
                  profile.learning_reason}
              </span>
            )}
            {profile.profession && (
              <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-medium text-blue-700">
                {profile.profession}
              </span>
            )}
            {(profile.destination_city || profile.destination_country) && (
              <span className="rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-medium text-green-700">
                {[profile.destination_city, profile.destination_country].filter(Boolean).join(', ')}
              </span>
            )}
            {profile.situations && profile.situations.length > 0 && (
              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-700">
                {profile.situations.length} situation{profile.situations.length !== 1 ? 's' : ''}
              </span>
            )}
            {profile.learning_focus && profile.learning_focus.length > 0 && (
              <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-medium text-rose-700">
                {profile.learning_focus
                  .map((code) => LEARNING_FOCUS.find((f) => f.code === code)?.name ?? code)
                  .join(', ')}
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Link
            to={`/workbook/${workbook.id}/preview`}
            className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm font-medium text-text-primary transition-colors hover:bg-gray-50"
          >
            <Eye size={16} />
            Preview
          </Link>

          <button
            onClick={downloadPdf}
            disabled={downloading || saving}
            className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm font-medium text-text-primary transition-colors hover:bg-gray-50 disabled:opacity-50"
            title={saving ? 'Save in progress…' : 'Download rendered PDF'}
          >
            {downloading ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
            {downloading ? 'Rendering…' : 'Download PDF'}
          </button>

          <button
            onClick={saveWorkbook}
            disabled={saving}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-dark disabled:opacity-50"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Comparison results (shown when a comparison regeneration completed) */}
      {comparisonResults.length > 0 && <ComparisonPanel results={comparisonResults} />}

      {/* Two-panel layout */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left panel — Block list */}
        <aside className="w-80 shrink-0 overflow-y-auto border-r border-border bg-white p-4">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-text-muted">
              Blocks ({blocks.length})
            </h3>
            <button
              onClick={() => setShowAddBlock(!showAddBlock)}
              className="flex items-center gap-1 rounded border border-border px-2 py-1 text-xs text-text-muted hover:border-brand hover:text-brand"
            >
              <Plus size={12} />
              Add
            </button>
          </div>

          {/* Add block picker */}
          {showAddBlock && <AddBlockPicker onClose={() => setShowAddBlock(false)} />}

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={blocks.map((b) => b.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-2">
                {blocks.map((block) => (
                  <div key={block.id} className="relative">
                    <SortableBlock
                      block={block}
                      isSelected={block.id === selectedBlockId}
                      onSelect={setSelectedBlockId}
                      onDelete={deleteBlock}
                    />
                    {/* Regenerate button (small overlay) */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        regenerateBlock(block.id, block.type as BlockType, block.title);
                      }}
                      disabled={regeneratingBlockId === block.id}
                      className="absolute right-8 top-1/2 -translate-y-1/2 rounded p-1 text-text-light opacity-0 transition-opacity hover:bg-blue-50 hover:text-blue-500 group-hover:opacity-100"
                      title="Regenerate this block"
                    >
                      {regeneratingBlockId === block.id ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <RefreshCw size={14} />
                      )}
                    </button>
                  </div>
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </aside>

        {/* Right panel — Block editor */}
        <div className="flex-1 overflow-y-auto p-6">
          {selectedBlock ? (
            <BlockEditor block={selectedBlock} onChange={updateBlock} />
          ) : (
            <div className="flex h-full items-center justify-center text-center">
              <div>
                <p className="text-lg font-medium text-text-muted">No block selected</p>
                <p className="mt-1 text-sm text-text-light">
                  Select a block from the list to edit it.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Comparison Panel — side-by-side results from two providers
// =============================================================================

function ComparisonPanel({ results }: { results: ComparisonResult[] }) {
  return (
    <div className="border-b border-border bg-gray-50 px-4 py-3">
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-text-muted">
        Provider Comparison
      </h3>
      <div className="grid grid-cols-2 gap-3">
        {results.map((r, i) => {
          const providerLabel = AI_PROVIDERS.find((p) => p.code === r.provider)?.name ?? r.provider;
          return (
            <div
              key={i}
              className={`rounded-lg border p-3 ${
                r.error ? 'border-red-200 bg-red-50' : 'border-border bg-white'
              }`}
            >
              {/* Provider badge */}
              <div className="mb-2 flex items-center justify-between">
                <span className="rounded bg-gray-100 px-2 py-0.5 text-xs font-semibold text-text-primary">
                  {providerLabel}
                </span>
                <span className="font-mono text-[10px] text-text-light">{r.model}</span>
              </div>

              {r.error ? (
                <p className="text-sm text-red-600">{r.error}</p>
              ) : r.block ? (
                <>
                  {/* Block title */}
                  <p className="mb-1 text-sm font-medium text-text-primary">{r.block.title}</p>
                  <p className="text-xs text-text-muted">Type: {r.block.type}</p>

                  {/* Stats */}
                  {r.tokens && (
                    <div className="mt-2 flex gap-3 text-[11px] text-text-light">
                      <span>
                        {r.tokens.input.toLocaleString()} in / {r.tokens.output.toLocaleString()}{' '}
                        out
                      </span>
                      <span>${r.tokens.estimated_cost_usd.toFixed(4)}</span>
                      <span>{(r.duration_ms / 1000).toFixed(1)}s</span>
                    </div>
                  )}
                </>
              ) : (
                <p className="text-sm text-text-light">No result</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// =============================================================================
// Add Block Picker — lets you pick a block type to add
// =============================================================================

function AddBlockPicker({ onClose }: { onClose: () => void }) {
  const { blocks, setBlocks } = useStudioStore();

  function handleAdd(type: BlockType) {
    const meta = BLOCK_META[type];
    const newBlock = {
      id: `studio-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      type,
      title: `New ${meta.name}`,
      order: blocks.length + 1,
    };

    // We add a minimal block — the user should regenerate or edit it
    setBlocks([...blocks, newBlock as unknown as Block]);
    onClose();
  }

  return (
    <div className="mb-3 rounded-lg border border-border bg-gray-50 p-2">
      <p className="mb-2 text-xs text-text-muted">Select a block type:</p>
      <div className="grid grid-cols-2 gap-1">
        {BLOCK_TYPES.map((type) => {
          const meta = BLOCK_META[type];
          return (
            <button
              key={type}
              onClick={() => handleAdd(type)}
              className="flex items-center gap-1.5 rounded px-2 py-1.5 text-left text-xs transition-colors hover:bg-white"
            >
              <span
                className="inline-block h-2 w-2 rounded-full"
                style={{ backgroundColor: meta.color }}
              />
              {meta.name}
            </button>
          );
        })}
      </div>
    </div>
  );
}
